import Vue from "vue";
import get from "lodash/get";
import router from "@/router";
import om from "../api";
import * as END_POINTS from "../config/constants/end-points";
import * as ACTIONS from "../config/constants/action-types";
import * as MUTATIONS from "../config/constants/mutation-types";
import {
  step1,
  step2,
  step4,
  stepsReactivo,
  stepsAgro
} from "../config/constants/steps";
import {
  ERROR_TECNICO,
  CLIENTE_ENCONTRADO,
  CLIENTE_FINANCIADO,
  INDIVIDUO_NEGATIVO,
  CUIT_NOVALIDA
} from "../config/constants/step-constants";
import {
  reactivoCodRet,
  reactivoProductoKey,
  sucursales
} from "../config/constants";
import { formatCuitReactivo, formattedFacturation } from "../utils";
import { provinciasAgro } from "@/config/constants";
import { departamentosAgro } from "@/config/constants";
import { createAgroError } from "@/errors/error-constants";

function handleEsClienteError(context, params) {
  context.commit(MUTATIONS.ON_IS_LOADING, false);
  const errorData = createAgroError("ES_CLIENTE_ERROR", {
    actionPayload: params.cuit,
    successAction: ACTIONS.DO_ES_CLIENTE_OK,
    returnRoute: "/agropaso1"
  });
  context.dispatch(ACTIONS.DO_AGRO_MOTOR_ERROR, errorData);
}

export default {
  /**
   * Action disparada por el router en
   * cada actualización
   * @param {Object} context
   * @param {Object} payload
   */
  [ACTIONS.DO_ROUTE](context, payload) {
    context.commit(MUTATIONS.ON_ROUTE, payload);
    setTimeout(() => {
      // Detener loading al ingresar a ruta nueva
      // Lo hacemos en un timeout = 0 para hacer
      // al final del event loop
      context.commit(MUTATIONS.ON_IS_LOADING, false);
    }, 0);
  },
  [ACTIONS.DO_ROUTE_PUSH](context, ruta) {
    if (ruta !== undefined) {
      router.push(ruta);
    } else {
      context.dispatch(ACTIONS.DO_MENSAJE, ERROR_TECNICO);
    }
  },
  /**
   * Mostrar u ocultar el loader
   * @param {Object} context
   * @param {Object} payload
   */
  [ACTIONS.DO_IS_LOADING](context, payload) {
    context.commit(MUTATIONS.ON_IS_LOADING, payload);
  },
  /**
   * Action genérico para llamadas a la API
   * Se debe pasar el endpoint, el action a ejecutar en caso
   * de éxito y el action en caso de error
   * @param {Object} context Vue Context
   * @param {endPoint, payload, doSuccess, doError} actionPayload
   */
  [ACTIONS.DO_REQUEST_API](context, { endPoint, payload, doSuccess, doError }) {
    return om
      .request({ endPoint, body: payload, mock: context.state.mock })
      .then(response => {
        if (doSuccess !== undefined) {
          return context.dispatch(doSuccess, response.data);
        }
        return response.data;
      })
      .catch(error => {
        if (doError !== undefined) {
          context.dispatch(doError, error);
        } else {
          context.dispatch(ACTIONS.DO_PASO_ERROR, error);
        }
        throw error;
      });
  },
  [ACTIONS.DO_GET_API](context, { endPoint, params, doSuccess, doError }) {
    return om
      .get(endPoint, params, context.state.mock)
      .then(response => {
        if (doSuccess !== undefined) {
          context.dispatch(doSuccess, response.data); //generalmente aca lo unico que hace es setear en el store
        }
      })
      .catch(error => {
        if (doError !== undefined) {
          context.dispatch(doError, error);
        } else {
          context.dispatch(ACTIONS.DO_PASO_ERROR, error);
        }
      });
  },
  /**
   * Actions para obtener los datos de carro abandonado
   * @param {Object} context Vue Context
   * @param {String} carro hash para el CA
   */
  [ACTIONS.DO_GET_CA](context, carro) {
    context.dispatch(ACTIONS.DO_REQUEST_API, {
      endPoint: END_POINTS.CA_GET,
      doSuccess: ACTIONS.DO_GET_CA_SUCCESS,
      doError: ACTIONS.DO_GET_CA_ERROR,
      payload: { randomKey: carro }
    });
  },
  [ACTIONS.DO_GET_CA_SUCCESS](context, payload) {
    context.commit(MUTATIONS.ON_GET_CA, payload);
    // Siempre que vengamos a consultar CA, es porque estamos APROBADOS
    context.dispatch(ACTIONS.DO_ROUTE_PUSH, step1.APROBADO);
  },
  [ACTIONS.DO_GET_CA_ERROR](context) {
    // Siempre que vengamos a consultar CA, es porque estamos APROBADOS
    context.dispatch(ACTIONS.DO_ROUTE_PUSH, step1.APROBADO);
  },
  /**
   * Actions para guardar los datos en carro abandonado
   * @param {Object} context Vue Context
   * @param {Object} carro hash para el CA
   */
  [ACTIONS.DO_SET_CA](context, payload) {
    let carro = get(context, "state.responseConsulta.respuesta.carro", null);
    let { paso1 } = context.state;
    let carroPayload = { ...paso1, ...payload };
    delete carroPayload.randomKey;

    context.dispatch(ACTIONS.DO_REQUEST_API, {
      endPoint: END_POINTS.CA_SET,
      payload: {
        randomKey: carro,
        paso: "paso2", // siempre guardamos en este paso
        datos: JSON.stringify(carroPayload)
      }
    });
  },
  /**
   * Actions disparadas por el
   * step navigator para avanzar pasos
   * Cada action guarda el paso
   * actual en el store
   * @param {Object} context
   */
  [ACTIONS.DO_LANDING]: (context, payload) => {
    context.commit(MUTATIONS.ON_IS_LOADING, true);
    context.commit(MUTATIONS.ON_LANDING, payload);
    setTimeout(() => {
      context.dispatch(ACTIONS.DO_ROUTE_PUSH, "paso1");
    }, 1000);
  },
  [ACTIONS.DO_PASO1]: (context, payload) => {
    context.commit(MUTATIONS.ON_IS_LOADING, true);
    context.commit(MUTATIONS.ON_PASO_1, payload);
    payload.nombreYApellido = `${payload.nombre} ${payload.apellido}`;
    payload.telefono = `${payload.phone.area}${payload.phone.numero}`;
    payload.cuit = payload.cuit.replace(/\D/g, "");
    payload.origen = context.state.origen;

    context.dispatch(ACTIONS.DO_REQUEST_API, {
      endPoint: END_POINTS.EMPRESA_CONSULTA,
      doSuccess: ACTIONS.DO_PASO1_SUCCESS,
      payload
    });
  },
  [ACTIONS.DO_PASO1_SUCCESS]: (context, payload) => {
    if (payload.respuesta) {
      const { respuesta } = payload.respuesta;

      if (respuesta) {
        if (step1[respuesta] === step1.APROBADO) {
          context.commit(MUTATIONS.ON_PASO_1_SUCCESS, payload);
          // En este paso consultamos el carro abandonado
          // y este action indica al router el paso siguiente
          context.dispatch(ACTIONS.DO_GET_CA, payload.respuesta.carro);
        } else {
          context.dispatch(ACTIONS.DO_MENSAJE, step1[respuesta]);
        }
      } else {
        context.dispatch(ACTIONS.DO_MENSAJE, ERROR_TECNICO);
      }
    } else {
      context.dispatch(ACTIONS.DO_MENSAJE, ERROR_TECNICO);
    }
  },
  [ACTIONS.DO_PASO2]: (context, payload) => {
    const {
      origen,
      paso1,
      responseConsulta: {
        respuesta: { randomKey }
      }
    } = context.state;

    const mergedPayload = {
      ...payload,
      ...paso1,
      ...{ randomKey },
      ...{ origen }
    };

    context.commit(MUTATIONS.ON_IS_LOADING, true);
    context.commit(MUTATIONS.ON_PASO_2, mergedPayload);
    context.dispatch(ACTIONS.DO_SET_CA, mergedPayload);

    const formattedPayload = formattedFacturation(mergedPayload);
    context.dispatch(ACTIONS.DO_REQUEST_API, {
      endPoint: END_POINTS.OBTENER_OFERTA,
      doSuccess: ACTIONS.DO_PASO2_SUCCESS,
      payload: formattedPayload
    });
  },
  [ACTIONS.DO_PASO2_SUCCESS]: (context, payload) => {
    if (payload.respuesta) {
      const { estado } = payload.respuesta.oferta;
      const isExceded = step2[estado] === step2.EXCEDIDO;
      const isApproved = isExceded || step2[estado] === step2.APROBADO;

      if (isApproved) {
        if (isExceded) {
          context.commit(MUTATIONS.ON_IS_EXCEEDED, true);
        }
        context.commit(MUTATIONS.ON_PASO_2_SUCCESS, payload);
        context.dispatch(ACTIONS.DO_ROUTE_PUSH, step2[estado]);
      } else {
        context.dispatch(ACTIONS.DO_MENSAJE, step2[estado]);
      }
    } else {
      context.dispatch(ACTIONS.DO_MENSAJE, ERROR_TECNICO);
    }
  },
  [ACTIONS.DO_PASO3]: context => {
    context.dispatch(ACTIONS.DO_ROUTE_PUSH, "paso4");
  },
  [ACTIONS.DO_PASO4]: (context, payload) => {
    const {
      responseOferta: {
        respuesta: { randomKey }
      }
    } = context.state;
    const telefono = `${payload.phone.area}${payload.phone.numero}`;
    const mergedPayload = { ...payload, ...{ randomKey }, ...{ telefono } };

    context.commit(MUTATIONS.ON_IS_LOADING, true);
    context.commit(MUTATIONS.ON_PASO_4, mergedPayload);
    const sucursal = sucursales.filter(
      sucursal => sucursal.numeroSucursal === mergedPayload.numSucursal
    )[0];
    mergedPayload.sucursal = JSON.stringify(sucursal);
    context.dispatch(ACTIONS.DO_REQUEST_API, {
      endPoint: END_POINTS.CONFIRMAR,
      doSuccess: ACTIONS.DO_PASO4_SUCCESS,
      payload: mergedPayload
    });
  },
  [ACTIONS.DO_PASO4_SUCCESS]: (context, payload) => {
    const { status } = payload;
    if (status && step4[status] === step4["ERROR"]) {
      context.dispatch(ACTIONS.DO_MENSAJE, step4[status]);
    } else {
      context.dispatch(ACTIONS.DO_ROUTE_PUSH, step4["GRACIAS"]);
    }
  },
  [ACTIONS.DO_PASO_ERROR]: (context, payload) => {
    Vue.$log.error(payload);
    context.dispatch(ACTIONS.DO_MENSAJE, ERROR_TECNICO);
  },
  [ACTIONS.DO_GRACIAS]: (context, payload) => {
    context.commit(MUTATIONS.ON_GRACIAS, payload);
  },
  /**
   * Mostrar un mensaje de error
   * @param {Object} context
   * @param {Object} payload Código del mensaje
   */
  [ACTIONS.DO_MENSAJE]: (context, payload) => {
    router.push({
      name: "mensaje",
      params: {
        mje: payload || ERROR_TECNICO
      }
    });
  },
  [ACTIONS.DO_CLICK_TRACKING]: (context, payload) => {
    // El evento de click solo debe trackearse con el primer elemento
    // que interactúa
    if (context.state.trackClickElement === null) {
      context.commit(MUTATIONS.ADOBE_APP_STARTED, payload);
    }
  },
  [ACTIONS.DO_MOCK]: (context, payload) => {
    context.commit(MUTATIONS.ON_MOCK, payload);
  },

  /** Reactivo Web */
  [ACTIONS.DO_REACTIVO_PASO1]: (context, payload) => {
    context.commit(MUTATIONS.ON_IS_LOADING, true);
    context.commit(MUTATIONS.ON_REACTIVO_PASO1, payload);

    const body = {
      cuit: formatCuitReactivo(get(payload, "cuit", "")),
      nombre: get(payload, "nombre", ""),
      apellido: get(payload, "apellido", ""),
      email: get(payload, "email", ""),
      telefono: get(payload, "landPhone.numero", ""),
      docRef: get(payload, "cuitReferidor", "")
        ? formatCuitReactivo(get(payload, "cuitReferidor", ""))
        : "",
      nomRef: get(payload, "razonSocialRef", ""),
      tipRel: get(payload, "relacion", ""),
      mailObe: get(payload, "mailRef", ""),
      origen: context.state.origen
    };

    context.dispatch(ACTIONS.DO_REQUEST_API, {
      endPoint: END_POINTS.RW_OBTENER_OFERTA,
      doSuccess: ACTIONS.DO_REACTIVO_PASO1_SUCCESS,
      payload: body
    });
  },
  [ACTIONS.DO_REACTIVO_PASO1_SUCCESS]: (context, response) => {
    context.commit(MUTATIONS.ON_IS_LOADING, false);
    const { codRet } = response.respuesta;
    switch (codRet) {
      case reactivoCodRet.PRECALI:
        context.commit(MUTATIONS.ON_REACTIVO_OFERTA, response);
        context.dispatch(ACTIONS.DO_ROUTE_PUSH, stepsReactivo.OFERTA);
        break;
      case reactivoCodRet.CUIT_DEUDA:
        context.dispatch(ACTIONS.DO_MENSAJE, CLIENTE_ENCONTRADO);
        break;
      case reactivoCodRet.CLIENTE_CON_CALI:
        context.dispatch(ACTIONS.DO_MENSAJE, CLIENTE_FINANCIADO);
        break;
      case reactivoCodRet.YA_SIMULO:
        context.dispatch(ACTIONS.DO_MENSAJE, INDIVIDUO_NEGATIVO);
        break;
      case reactivoCodRet.ERROR_FILTROS_AAII:
        context.dispatch(ACTIONS.DO_ROUTE_PUSH, stepsReactivo.ERROR_OFERTA);
        break;
      case reactivoCodRet.EXCEDIDO:
        context.commit(MUTATIONS.ON_REACTIVO_EXCEDIDO);
        context.dispatch(ACTIONS.DO_ROUTE_PUSH, stepsReactivo.GRACIAS);
        break;
      default:
        context.dispatch(ACTIONS.DO_MENSAJE, ERROR_TECNICO);
        break;
    }
  },
  [ACTIONS.DO_REACTIVO_OFERTA]: (context, payload) => {
    context.commit(MUTATIONS.ON_IS_LOADING, true);
    context.commit(MUTATIONS.ON_REACTIVO_PASO1, payload);

    const paso1 = context.getters.getPaso1Reactivo();
    const oferta = context.getters.getOfertaReactivo();
    const idSucursal = (payload.sucursal.id + "").padStart(4, "0");

    const body = {
      email: paso1.email,
      sucursal: `(${idSucursal}) ${payload.sucursal.direccion}, ${payload.sucursal.localidad}`,
      cuit: formatCuitReactivo(paso1.cuit),
      razonSocial: oferta.razonSocial,
      nombreYApellido: paso1.nombre + " " + paso1.apellido,
      telefonoTipo: paso1.landPhone.tipo,
      telefonoNumero: paso1.landPhone.numero,
      preCalificacion: `$${String(payload.oferta)}`,
      monto: `$${String(payload.monto)}`,
      tarjeta: `$${String(payload.values.visaBusiness)}`,
      acuerdo: `$${String(payload.values.cuentaCorriente)}`,
      prestamo: `$${String(payload.values.prestamoFinanciero)}`,
      prestamoSGR: `$${String(payload.values.prestamoLP)}`,
      descuentoCheque: `$${String(payload.values.descuentoCheques)}`,
      codPr01: reactivoProductoKey("cuentaCorriente"),
      monto01: String(payload.values.cuentaCorriente).padStart(15, "0"),
      codPr02: reactivoProductoKey("prestamoFinanciero"),
      monto02: String(payload.values.prestamoFinanciero).padStart(15, "0"),
      codPr03: reactivoProductoKey("visaBusiness"),
      monto03: String(payload.values.visaBusiness).padStart(15, "0"),
      codPr04: reactivoProductoKey("descuentoCheques"),
      monto04: String(payload.values.descuentoCheques).padStart(15, "0"),
      codPr05: reactivoProductoKey("prestamoLP"), //se pone directo esto, desde BACK llega como PFSG y se requiere para PY52 SGR
      monto05: String(payload.values.prestamoLP).padStart(15, "0"),
      codPr06: "",
      monto06: "",
      codPr07: "",
      monto07: "",
      codPr08: "",
      monto08: "",
      codPr09: "",
      monto09: "",
      codPr10: "",
      monto10: "",
      codPr11: "",
      monto11: "",
      codPr12: "",
      monto12: "",
      codPr13: "",
      monto13: "",
      codPr14: "",
      monto14: "",
      codPr15: "",
      monto15: "",
      sucDest: payload.sucursal.id.padStart(4, "0")
    };

    context.dispatch(ACTIONS.DO_REQUEST_API, {
      endPoint: END_POINTS.RW_CONFIRMAR_OFERTA,
      doSuccess: ACTIONS.DO_REACTIVO_OFERTA_SUCCESS,
      payload: body
    });
  },
  [ACTIONS.DO_REACTIVO_GRACIAS_EXCEDIDO]: (context, payload) => {
    context.commit(MUTATIONS.ON_IS_LOADING, true);
    const paso1 = context.getters.getPaso1Reactivo();
    const oferta = context.getters.getOfertaReactivo();
    const idSucursal = (payload.sucursal.id + "").padStart(4, "0");

    const body = {
      email: paso1.email,
      sucursal: `(${idSucursal}) ${payload.sucursal.direccion}, ${payload.sucursal.localidad}`,
      cuit: formatCuitReactivo(paso1.cuit),
      razonSocial: oferta.razonSocial,
      nombreYApellido: paso1.nombre + " " + paso1.apellido,
      telefonoTipo: paso1.landPhone.tipo,
      telefonoNumero: paso1.landPhone.numero,
      preCalificacion: "",
      monto: "",
      tarjeta: "",
      acuerdo: "",
      prestamo: "",
      prestamoSGR: "",
      descuentoCheque: "",
      codPr01: "",
      monto01: "",
      codPr02: "",
      monto02: "",
      codPr03: "",
      monto03: "",
      codPr04: "",
      monto04: "",
      codPr05: "",
      monto05: "",
      codPr06: "",
      monto06: "",
      codPr07: "",
      monto07: "",
      codPr08: "",
      monto08: "",
      codPr09: "",
      monto09: "",
      codPr10: "",
      monto10: "",
      codPr11: "",
      monto11: "",
      codPr12: "",
      monto12: "",
      codPr13: "",
      monto13: "",
      codPr14: "",
      monto14: "",
      codPr15: "",
      monto15: "",
      opcion: "T",
      sucDest: payload.sucursal.id.padStart(4, "0")
    };

    context.dispatch(ACTIONS.DO_REQUEST_API, {
      endPoint: END_POINTS.RW_CONFIRMAR_OFERTA,
      doSuccess: ACTIONS.DO_REACTIVO_GRACIAS_EXCEDIDO_SUCCESS,
      doError: ACTIONS.DO_REACTIVO_GRACIAS_EXCEDIDO_ERROR,
      payload: body
    });
  },
  [ACTIONS.DO_REACTIVO_GRACIAS_EXCEDIDO_SUCCESS]: context => {
    context.commit(MUTATIONS.ON_CONFIRMACION_SUCURSAL, true);
    context.commit(MUTATIONS.ON_IS_LOADING, false);
  },

  [ACTIONS.DO_REACTIVO_GRACIAS_EXCEDIDO_ERROR]: context => {
    context.commit(MUTATIONS.ON_CONFIRMACION_SUCURSAL, false);
    context.commit(MUTATIONS.ON_IS_LOADING, false);
  },

  [ACTIONS.DO_REACTIVO_OFERTA_SUCCESS]: (context, response) => {
    context.commit(MUTATIONS.ON_IS_LOADING, false);
    const codRet = response.respuesta;
    if (codRet === "00") {
      context.dispatch(ACTIONS.DO_ROUTE_PUSH, stepsReactivo.GRACIAS);
      return;
    }
    context.dispatch(ACTIONS.DO_MENSAJE, ERROR_TECNICO);
  },

  /** Agro */
  [ACTIONS.DO_AGRO_PASO_1]: context => {
    context.dispatch(ACTIONS.DO_ROUTE_PUSH, stepsAgro.AGRO_PASO_2);
  },

  [ACTIONS.DO_AGRO_PASO_1_VALIDAR_CUIT]: (context, payload) => {
    return context.dispatch(ACTIONS.DO_GET_API, {
      endPoint: END_POINTS.VALIDAR_CUIT.concat(payload),
      doSuccess: ACTIONS.DO_AGRO_PASO_1_VALIDAR_CUIT_SUCCESS,
      doError: ACTIONS.DO_AGRO_PASO_1_VALIDAR_CUIT_ERROR
    });
  },

  [ACTIONS.DO_AGRO_PASO_1_GET_VALIDAR_CUIT]: async (context, payload) => {
    try {
      context.commit(MUTATIONS.ON_IS_LOADING, true);
      const response = await om.get(END_POINTS.VALIDAR_CUIT.concat(payload));
      context.commit(MUTATIONS.ON_IS_LOADING, false);
      return response;
    } catch (error) {
      context.commit(MUTATIONS.ON_IS_LOADING, false);
      throw error;
    }
  },

  [ACTIONS.DO_AGRO_PASO_1_VALIDAR_CUIT_SUCCESS]: (context, response) => {
    context.commit(MUTATIONS.ON_VALIDAR_CUIT_AGRO, response);
    return response;
  },
  [ACTIONS.DO_AGRO_PASO_2]: context => {
    context.dispatch(ACTIONS.DO_ROUTE_PUSH, stepsAgro.AGRO_PASO_3);
  },
  [ACTIONS.DO_AGRO_PASO_1_VALIDAR_CUIT_ERROR]: context => {
    context.dispatch(ACTIONS.DO_MENSAJE, CUIT_NOVALIDA);
  },

  [ACTIONS.DO_AGRO_PASO_3_0]: context => {
    context.dispatch(ACTIONS.DO_ROUTE_PUSH, stepsAgro.AGRO_PASO_4);
  },

  /*ESTE ES EL ULTIMO PASO, CON TODA LA DATA DEL FRONT (REVISAR PARA CARGARLA EN STORE)*/

  [ACTIONS.DO_AGRO_PASO_3]: async (context, payload) => {
    context.commit(MUTATIONS.ON_IS_LOADING, true);
    let codProvincia = payload.datosProduccion.provincia;

    let provincia = provinciasAgro.filter(obj => {
      return obj.value === codProvincia;
    });

    let departamentos = departamentosAgro.otra;
    if (codProvincia === "02") {
      departamentos = departamentosAgro.buenosAires;
    }
    if (codProvincia === "04") {
      departamentos = departamentosAgro.cordoba;
    }
    if (codProvincia === "08") {
      departamentos = departamentosAgro.entreRios;
    }
    if (codProvincia === "11") {
      departamentos = departamentosAgro.laPampa;
    }
    if (codProvincia === "21") {
      departamentos = departamentosAgro.santaFe;
    }

    let departamento = departamentos.filter(obj => {
      return obj.value === payload.datosProduccion.departamento;
    });

    let cultivos = [];

    let stockSoja2 = payload.datosProduccion.granos.sojaPrimera
      ? "0"
      : payload.datosProduccion.stock.sojaPrimera;

    let stockMaiz2 = payload.datosProduccion.granos.maizPrimera
      ? "0"
      : payload.datosProduccion.stock.maizPrimera;

    if (payload.datosProduccion.granos.sojaPrimera) {
      let sojaPrimera = {
        tipo: "SOJA1",
        hectareas: payload.datosProduccion.hectareas.sojaPrimera,
        stock: payload.datosProduccion.stock.sojaPrimera
      };
      cultivos.push(sojaPrimera);
    }

    if (payload.datosProduccion.granos.sojaSegunda) {
      let sojaSegunda = {
        tipo: "SOJA2",
        hectareas: payload.datosProduccion.hectareas.sojaSegunda,
        stock: stockSoja2
      };
      cultivos.push(sojaSegunda);
    }

    if (payload.datosProduccion.granos.maizPrimera) {
      let maizPrimera = {
        tipo: "MAIZ1",
        hectareas: payload.datosProduccion.hectareas.maizPrimera,
        stock: payload.datosProduccion.stock.maizPrimera
      };
      cultivos.push(maizPrimera);
    }

    if (payload.datosProduccion.granos.maizSegunda) {
      let maizSegunda = {
        tipo: "MAIZ2",
        hectareas: payload.datosProduccion.hectareas.maizSegunda,
        stock: stockMaiz2
      };
      cultivos.push(maizSegunda);
    }

    if (payload.datosProduccion.granos.girasol) {
      let girasol = {
        tipo: "GIRAS",
        hectareas: payload.datosProduccion.hectareas.girasol,
        stock: payload.datosProduccion.stock.girasol
      };
      cultivos.push(girasol);
    }

    if (payload.datosProduccion.granos.trigoCebada) {
      let trigoCebada = {
        tipo: "TRIGO",
        hectareas: payload.datosProduccion.hectareas.trigoCebada,
        stock: payload.datosProduccion.stock.trigoCebada
      };
      cultivos.push(trigoCebada);
    }

    let porcentaje =
      payload.datosProduccion.hectareas.porcentaje <= 100
        ? payload.datosProduccion.hectareas.porcentaje
        : "100";

    const body = {
      nroDoc: payload.cuit,
      telefono: payload.datosPersonales.telefono,
      apellidoNombre:
        payload.datosPersonales.nombre + " " + payload.datosPersonales.apellido,
      email: payload.datosPersonales.email,
      provDepId: payload.datosProduccion.departamento,
      provDepNombre: departamento[0].label + " - " + provincia[0].label,
      cultivos: cultivos,
      hectareasPropias: porcentaje,
      ganadoStock: payload.datosProduccion.cabezasDeGanado,
      deudaCorto: payload.datosProduccion.stock.deudaCorto + "000000",
      deudaLargo: payload.datosProduccion.stock.deudaLargo + "000000",
      razonSocial: payload.razonSocial || "",
      codigoActividad: payload.idActividad || " ",
      actividad: payload.descripcionActividad || " ",
      boldHectareas: payload.boldHectareas || "",
      boldRespuesta: payload.boldRespuesta || "",
      geolocalizacion: payload.geolocalizacion || "",
      cliente: context.state.agroEsCliente ? "SI" : "NO"
    };

    await context.dispatch(ACTIONS.DO_REQUEST_API, {
      endPoint: END_POINTS.RW_OBTENER_OFERTA_AGRO,
      doSuccess: ACTIONS.DO_AGRO_PASO_3_SUCCESS,
      payload: body
    });
    context.commit(MUTATIONS.ON_IS_LOADING, false);
  },

  [ACTIONS.DO_AGRO_PASO_3_SUCCESS]: (context, response) => {
    context.commit(MUTATIONS.ON_OFERTA_AGRO, response.respuesta);
    if (
      (response.respuesta.ofertaCorto.length < 7 ||
        response.respuesta.ofertaCorto === "") &&
      (response.respuesta.ofertaLargo.length < 7 ||
        response.respuesta.ofertaLargo === "") &&
      (response.respuesta.ofertaSenasa.length < 7 ||
        response.respuesta.ofertaSenasa === "")
    ) {
      context.dispatch(ACTIONS.DO_ROUTE_PUSH, stepsAgro.AGRO_SIN_OFERTA);
    } else {
      context.dispatch(ACTIONS.DO_ROUTE_PUSH, stepsAgro.AGRO_PASO_5);
    }
  },

  [ACTIONS.DO_CALL_API_ES_CLIENTE]: async (context, cuit) => {
    context.commit(MUTATIONS.ON_IS_LOADING, true);
    try {
      const response = await om.request({
        endPoint: END_POINTS.AGRO_ES_CLIENTE,
        body: { cuit: cuit }
      });
      context.commit(MUTATIONS.ON_IS_LOADING, false);
      return response;
    } catch (error) {
      context.commit(MUTATIONS.ON_IS_LOADING, false);
      throw error;
    }
  },

  [ACTIONS.DO_ES_CLIENTE]: async (context, params) => {
    context.commit(MUTATIONS.ON_IS_LOADING, true);
    try {
      const response = await om.request({
        endPoint: END_POINTS.AGRO_ES_CLIENTE,
        body: { cuit: params.cuit }
      });
      const data = response.data;
      if (data.status === "ERROR") {
        handleEsClienteError(context, params);
      } else {
        context.dispatch(ACTIONS.DO_ES_CLIENTE_OK, {
          response,
          cuit: params.cuit
        });
      }
    } catch (error) {
      handleEsClienteError(context, params);
    }
  },

  [ACTIONS.DO_ES_CLIENTE_OK]: async (context, { response, cuit }) => {
    const data = response.data;
    console.log("esCliente response:", data);
    if (data.status === "OK") {
      const esCliente = data.respuesta;
      if (esCliente) {
        context.commit(MUTATIONS.ON_AGRO_ES_CLIENTE, true);
        try {
          const grantingResponse = await om.request({
            endPoint: END_POINTS.AGRO_GRANTING_TICKET,
            body: {
              authentication: {
                userID: "ZG16027",
                consumerID: "16000027",
                authenticationType: "61",
                authenticationData: []
              },
              backendUserRequest: {
                userId: "",
                accessCode: "16000027",
                dialogId: ""
              }
            }
          });
          const tsec = grantingResponse.headers["tsec"];
          console.log("tsec:", tsec);

          const qualificationsResponse = await om.get(
            END_POINTS.AGRO_AVAILABLE_QUALIFICATIONS,
            { taxId: cuit },
            null,
            { tsec, "Content-Type": "application/json" }
          );
          console.log("qualifications:", qualificationsResponse.data);

          const hasQualification =
            qualificationsResponse.data?.data?.[0]?.hasQualification;
          context.commit(MUTATIONS.ON_IS_LOADING, false);
          if (hasQualification) {
            context.dispatch(ACTIONS.DO_ROUTE_PUSH, stepsAgro.AGRO_ES_CLIENTE);
          } else {
            context.dispatch(ACTIONS.DO_ROUTE_PUSH, stepsAgro.PATH_AGRO_PASO_2);
          }
        } catch (e) {
          console.error("Error en grantingTicket/qualifications:", e);
          context.commit(MUTATIONS.ON_IS_LOADING, false);
          context.dispatch(ACTIONS.DO_ROUTE_PUSH, stepsAgro.AGRO_ES_CLIENTE);
        }
      } else {
        context.commit(MUTATIONS.ON_AGRO_ES_CLIENTE, false);
        context.commit(MUTATIONS.ON_IS_LOADING, false);
        context.dispatch(ACTIONS.DO_ROUTE_PUSH, stepsAgro.PATH_AGRO_PASO_2);
      }
    } else {
      context.dispatch(ACTIONS.DO_PASO_ERROR);
    }
  },

  /**
   * Action que permite a Adobe Target inyectar en el store
   * el código de la actividad que debe seleccionarse
   * @uso (desde afuera): BBVA_TC.$store.dispatch('setAdobeTargetExperience', 'AR062019_B')
   */
  [ACTIONS.DO_ADOBE_TARGET_EXPERIENCE]: (context, payload) => {
    context.commit(MUTATIONS.ON_ADOBE_TARGET_EXPERIENCE, payload);
  },

  [ACTIONS.DO_GENERAR_OTP]: async (context, payload) => {
    let actionResponse = {
      hasError: false,
      isBlocked: false,
      requestId: null
    };

    try {
      const response = await om.request({
        endPoint: END_POINTS.GENERAR_OTP,
        body: payload
      });
      const data = response.data;
      if (data.status === "ERROR") {
        if (data.respuesta !== undefined) {
          if (data.respuesta.toLowerCase().includes("usuario bloqueado")) {
            actionResponse.isBlocked = true;
          }
        }
        actionResponse.hasError = true;
      } else if (data.respuesta?.data?.requestId) {
        actionResponse.requestId = data.respuesta.data.requestId;
      } else {
        actionResponse.hasError = true;
      }
    } catch (e) {
      actionResponse.hasError = true;
    }
    return actionResponse;
  },

  [ACTIONS.DO_GENERAR_OTP_SUCCESS]: (context, response) => {
    // Este ya no es necesario llamarlo dentro de DO_GENERAR_OTP
    Vue.$log.info("OTP generado exitosamente", response);
    context.commit(MUTATIONS.ON_GENERAR_OTP_SUCCESS, response);
    return response;
  },

  [ACTIONS.DO_GENERAR_OTP_ERROR]: (context, error) => {
    Vue.$log.error("Error al generar OTP", error);
    throw error;
  },

  [ACTIONS.DO_VALIDAR_OTP]: (context, payload) => {
    return om
      .request({
        endPoint: END_POINTS.VALIDAR_OTP,
        body: payload,
        mock: context.state.mock
      })
      .then(response => {
        return response.data;
      })
      .catch(error => {
        throw error;
      });
  },

  [ACTIONS.DO_VALIDAR_OTP_SUCCESS]: (context, response) => {
    Vue.$log.info("OTP validado exitosamente", response);
    return response;
  },

  [ACTIONS.DO_VALIDAR_OTP_ERROR]: (context, error) => {
    Vue.$log.error("Error al validar OTP", error);
    throw error;
  },

  [ACTIONS.DO_AGRO_CONSULTAR_CULTIVOS]: (context, payload) => {
    // NO activar loading global - el componente maneja su propio loading
    // context.commit(MUTATIONS.ON_IS_LOADING, true);

    const body = {
      cuit: payload.cuit,
      geometria: payload.geometria
    };

    return context.dispatch(ACTIONS.DO_REQUEST_API, {
      endPoint: END_POINTS.AGRO_BOLD_CULTIVOS,
      doSuccess: ACTIONS.DO_AGRO_CONSULTAR_CULTIVOS_SUCCESS,
      doError: ACTIONS.DO_AGRO_CONSULTAR_CULTIVOS_ERROR,
      payload: body
    });
  },

  [ACTIONS.DO_AGRO_CONSULTAR_CULTIVOS_SUCCESS]: (context, response) => {
    // NO desactivar loading global ya que no lo activamos
    // context.commit(MUTATIONS.ON_IS_LOADING, false);
    Vue.$log.info("Consulta de cultivos exitosa", response);
    // Aquí puedes guardar la respuesta en el store si es necesario
    // context.commit(MUTATIONS.ON_AGRO_CULTIVOS_DATA, response);
    return response;
  },

  [ACTIONS.DO_AGRO_CONSULTAR_CULTIVOS_ERROR]: (context, error) => {
    // NO desactivar loading global ya que no lo activamos
    // context.commit(MUTATIONS.ON_IS_LOADING, false);
    Vue.$log.error("Error al consultar cultivos", error);
    throw error;
  },
  /**
   * Navegar a la pantalla de error de Agro guardando datos en Vuex
   * @param {Object} context Vue Context
   * @param {Object} errorData Datos del error (desde error-constants)
   */
  [ACTIONS.DO_AGRO_MOTOR_ERROR]: (context, errorData) => {
    context.commit(MUTATIONS.SET_AGRO_ERROR, errorData);
    router.push({ name: "agroError" });
  }
};
